import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Newsletter() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, { email, source: "newsletter-footer" });
      setDone(true);
    } catch {
      toast.error(t("cart.error"));
    }
  };

  if (done) {
    return (
      <p className="mt-5 text-sm font-light text-[#faf8f5]/80" data-testid="footer-newsletter-thanks">
        {t("footer.newsletter.thanks")}
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="mt-5 flex max-w-sm" data-testid="footer-newsletter">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("footer.newsletter.placeholder")}
        data-testid="footer-newsletter-email"
        className="w-full border border-[#faf8f5]/25 bg-transparent px-4 py-3 text-sm font-light text-[#faf8f5] placeholder:text-[#faf8f5]/35 outline-none focus:border-[#faf8f5]"
      />
      <button
        type="submit"
        data-testid="footer-newsletter-submit"
        aria-label={t("footer.newsletter.button")}
        className="border border-l-0 border-[#faf8f5]/25 px-4 text-[#faf8f5] transition-colors duration-300 hover:bg-[#faf8f5] hover:text-[#111111]"
      >
        <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
      </button>
    </form>
  );
}

export default function Footer() {
  const { t } = useLanguage();

  const nav = [
    { to: "/shop", key: "nav.prints" },
    { to: "/made-to-order", key: "nav.mto" },
    { to: "/art-walls", key: "aw.kicker" },
    { to: "/commission", key: "nav.commissions" },
    { to: "/exhibition", key: "nav.exhibitions" },
    { to: "/about", key: "nav.concept" },
    { to: "/journal", key: "journal.kicker" },
  ];
  const legal = [
    { to: "/legal/shipping-returns", key: "footer.legal.shipping" },
    { to: "/legal/cgv", key: "footer.legal.cgv" },
    { to: "/legal/mentions-legales", key: "footer.legal.mentions" },
    { to: "/legal/privacy", key: "footer.legal.privacy" },
    { to: "/legal/cookies", key: "footer.legal.cookies" },
    { to: "/legal/withdrawal", key: "footer.legal.withdrawal" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-[#111111] text-[#faf8f5] py-16 md:py-20">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="font-display text-2xl md:text-3xl tracking-tight">
              Imaginary <span className="italic">Collection</span>
            </p>
            <p className="mt-4 max-w-sm font-light text-[#faf8f5]/60">
              The X Goes Here — the art you dream of owning.
            </p>
            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.25em] text-[#faf8f5]/50">
                {t("footer.newsletter.title")}
              </p>
              <p className="mt-2 max-w-sm text-sm font-light text-[#faf8f5]/60">
                {t("footer.newsletter.body")}
              </p>
              <Newsletter />
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] text-[#faf8f5]/50">
              {t("footer.col.navigate")}
            </p>
            <ul className="mt-5 space-y-3 text-sm font-light text-[#faf8f5]/70">
              {nav.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-underline hover:text-[#faf8f5]">
                    {t(l.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] text-[#faf8f5]/50">
              {t("footer.col.contact")}
            </p>
            <ul className="mt-5 space-y-3 text-sm font-light text-[#faf8f5]/70">
              <li>
                <a href="mailto:hello@thexgoeshere.com" className="link-underline hover:text-[#faf8f5]" data-testid="footer-contact-email">
                  hello@thexgoeshere.com
                </a>
              </li>
              <li>
                <a
                  href="https://instagram.com/thexgoeshere"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 link-underline hover:text-[#faf8f5]"
                  data-testid="footer-instagram"
                >
                  <Instagram className="h-4 w-4" strokeWidth={1.5} /> Instagram
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] text-[#faf8f5]/50">
              {t("footer.col.legal")}
            </p>
            <ul className="mt-5 space-y-3 text-sm font-light text-[#faf8f5]/70">
              {legal.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-underline hover:text-[#faf8f5]" data-testid={`footer-${l.key}`}>
                    {t(l.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-[#faf8f5]/12 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-light text-[#faf8f5]/40">
          <span>© {new Date().getFullYear()} Imaginary Collection — Société NPDS</span>
          <span>Paris — Île-de-France</span>
        </div>
      </div>
    </footer>
  );
}
